/* eslint-disable react/prop-types */
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  useToast,
} from "randmarcomps";
import { CircleHelp, Sprout, Warehouse } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Product as RTKProduct, useGetV4PartnerByRouteApplicationIdProductAndRandmarSkuQuery } from "randmar-api-client";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { Product } from "../../stores/product/Product";
import CenteredLoader from "../util/CenteredLoader";
import InlineSpinner from "../util/InlineSpinner";
import AddProductToOpportunity from "./AddProductToOpportunity";
import AddToRequisition from "./AddToRequisition";
import ProductDescriptionTable from "./ProductDescriptionTable";

// Type guard to check if the product is the new RTK type
function isRTKProduct(product: Product | RTKProduct): product is RTKProduct {
  return !("source" in product);
}

function hasStatus(error: unknown): error is { status: number | string } {
  return typeof error === "object" && error !== null && "status" in error;
}

function hasMessage(error: unknown): error is { message?: unknown } {
  return typeof error === "object" && error !== null && "message" in error;
}

interface IProps {
  defaultProduct?: Product | RTKProduct;
  randmarSKU: string;
}

const ProductDetailsModal: React.FC<IProps> = ({ defaultProduct, randmarSKU }) => {
  const { applicationId } = useManufacturer();
  const [open, setOpen] = useState(false);
  const [addProductToOpportunityOpen, setAddProductToOpportunityOpen] = useState(false);
  const { toast } = useToast();

  const trimmedRandmarSku = randmarSKU.trim();
  const hasRandmarSku = trimmedRandmarSku.length > 0;

  const {
    data: rtkProduct,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetV4PartnerByRouteApplicationIdProductAndRandmarSkuQuery(
    { routeApplicationId: applicationId, randmarSku: trimmedRandmarSku, withSpecification: true },
    { skip: !open || !hasRandmarSku }
  );

  const handleButtonClicked = () => {
    if (!hasRandmarSku) return;
    setOpen(true);
  };

  const errorSummary = useMemo(() => {
    if (!isError) return null;

    if (hasStatus(error)) {
      const statusText = typeof error.status === "number" ? `HTTP ${error.status}` : String(error.status);
      return { key: `${trimmedRandmarSku}:${statusText}`, title: statusText, description: "Failed to load product details." };
    }

    const message = hasMessage(error) && typeof error.message === "string"
      ? error.message
      : "Failed to load product details.";

    return { key: `${trimmedRandmarSku}:${message}`, title: "Error", description: message };
  }, [error, isError, trimmedRandmarSku]);

  const lastToastKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      lastToastKeyRef.current = null;
      return;
    }
    if (!isError || !errorSummary) return;
    if (lastToastKeyRef.current === errorSummary.key) return;
    lastToastKeyRef.current = errorSummary.key;

    toast({
      title: errorSummary.title,
      description: errorSummary.description,
      variant: "destructive",
    });
  }, [errorSummary, isError, open, toast]);

  // Normalize the product data to the legacy `Product` type for child components
  const productToShow = useMemo((): Product | undefined => {
    if (rtkProduct) return Product.convertFromNewProduct(rtkProduct);
    if (!defaultProduct) return undefined;
    return isRTKProduct(defaultProduct) ? Product.convertFromNewProduct(defaultProduct) : defaultProduct;
  }, [defaultProduct, rtkProduct]);

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                variant="ghost"
                size="icon"
                disabled={open || !hasRandmarSku}
                onClick={handleButtonClicked}
                title={hasRandmarSku ? "View inventory" : "Inventory unavailable (missing SKU)"}
                aria-label={hasRandmarSku ? "View inventory" : "Inventory unavailable (missing SKU)"}
              >
                {isLoading ? (
                  <InlineSpinner size="md" decorative className="text-primary" />
                ) : (
                  <Warehouse className="text-primary" aria-hidden="true" />
                )}
              </Button>
            </span>
          </TooltipTrigger>
          {!hasRandmarSku ? (
            <TooltipContent>
              <p>Inventory unavailable: missing SKU.</p>
            </TooltipContent>
          ) : null}
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl" aria-busy={isLoading}>
          <DialogHeader>
            <DialogTitle className="text-center">
              {productToShow
                ? `${productToShow.manufacturerName} ${productToShow.mpn}`
                : `Inventory${trimmedRandmarSku ? ` (${trimmedRandmarSku})` : ""}`}
            </DialogTitle>
            <DialogDescription className="text-center">
              View inventory status across warehouses.
            </DialogDescription>
          </DialogHeader>

          {/* OpenOrdersSummary is a MobX component, so it's removed for now */}
          {/* <OpenOrdersSummary openOrders={openOrders.filter(o => o.randmarSKU === product?.randmarSKU)} /> */}

          {isLoading ? (
            <CenteredLoader text="Loading inventory…" variant="plain" className="min-h-[240px]" />
          ) : isError ? (
            <div className="min-h-[240px] space-y-3 rounded-2xl border border-gray-100 bg-white p-6">
              <p className="text-[15px] font-semibold text-slate-900">We couldn’t load this product.</p>
              <p className="text-[13px] text-slate-600">
                {trimmedRandmarSku ? `SKU: ${trimmedRandmarSku}` : "Missing SKU."}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={() => refetch()} className="rounded-xl">
                  Retry
                </Button>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">
                  Close
                </Button>
              </div>
            </div>
          ) : productToShow ? (
            <ProductDescriptionTable product={productToShow} defaultTabIndex={1} />
          ) : (
            <div className="min-h-[240px] space-y-2 rounded-2xl border border-gray-100 bg-white p-6">
              <p className="text-[15px] font-semibold text-slate-900">No product details available.</p>
              <p className="text-[13px] text-slate-600">Try again in a moment.</p>
            </div>
          )}

          <DialogFooter>
            {productToShow?.isManufacturer ? (
              <>
                <Button variant="outline" onClick={() => setAddProductToOpportunityOpen(true)}>
                  <Sprout className="mr-2 h-4 w-4" aria-hidden="true" />
                  <span>Add to Opportunity</span>
                </Button>

                <Button variant="outline">
                  <CircleHelp className="mr-2 h-4 w-4" aria-hidden="true" />
                  <AddToRequisition product={productToShow}>
                    <span>Add to Draft Purchase</span>
                  </AddToRequisition>
                </Button>
              </>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {productToShow && (
        <AddProductToOpportunity
          product={productToShow}
          open={addProductToOpportunityOpen}
          onOpenChange={setAddProductToOpportunityOpen}
        />
      )}
    </>
  );
};

export default ProductDetailsModal;
